import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Skeleton } from '../ui/skeleton'
import { format, parseISO } from 'date-fns'

interface VisitorsChartProps {
  data: Array<{
    date: string
    visitors: number
    pageviews: number
    [key: string]: any // For dynamic domain-specific data
  }>
  domains?: string[]
  isLoading?: boolean
}

export function VisitorsChart({ data, domains, isLoading }: VisitorsChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    )
  }

  const formattedData = (data || []).map((item) => {
    const parsed =
      item?.date && typeof item.date === 'string'
        ? parseISO(item.date)
        : new Date()
    const safeDate = isNaN(parsed.getTime()) ? new Date() : parsed

    return {
      ...item,
      date: format(safeDate, 'MMM dd'),
      visitors: typeof item.visitors === 'number' ? item.visitors : 0,
      pageviews: typeof item.pageviews === 'number' ? item.pageviews : 0,
    }
  })

  // Generate colors for domains (using HSL for better color distribution)
  const generateColor = (index: number, total: number) => {
    const hue = (index * 360) / Math.max(total, 1)
    return `hsl(${hue}, 70%, 50%)`
  }

  // Check if we have multi-domain data
  const hasMultipleDomains = domains && domains.length > 1

  return (
    <Card>
      <CardHeader>
        <CardTitle>Traffic Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={formattedData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="date"
              className="text-xs"
              tick={{ fill: 'currentColor' }}
            />
            <YAxis className="text-xs" tick={{ fill: 'currentColor' }} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px',
              }}
            />
            <Legend />

            {hasMultipleDomains ? (
              // Show separate lines for each domain
              <>
                {domains.map((domain, idx) => (
                  <Line
                    key={`${domain}_visitors`}
                    type="monotone"
                    dataKey={`${domain}_visitors`}
                    stroke={generateColor(idx, domains.length)}
                    name={`${domain} (Visitors)`}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </>
            ) : (
              // Show aggregated lines when single domain or no domain info
              <>
                <Line
                  type="monotone"
                  dataKey="visitors"
                  stroke="hsl(221.2 83.2% 53.3%)"
                  name="Visitors"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="pageviews"
                  stroke="hsl(142.1 76.2% 36.3%)"
                  name="Pageviews"
                  strokeWidth={2}
                  dot={false}
                />
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}
